/**
 * Spring animation configuration for smooth, Apple-like transitions
 * These values are carefully tuned to prevent flickering and ensure smooth animations
 * @type {Object}
 */
export const SPRING_ANIMATION = {
  type: "spring",      // Using spring physics for natural motion
  stiffness: 300,      // Controls the spring's force (lower = smoother)
  damping: 25,         // Controls how quickly the spring settles (lower = more bounce)
  mass: 1,             // Affects the weight feel of the animation (higher = more stable)
  velocity: 0,         // Initial velocity of the animation
  restDelta: 0.001     // Precision threshold for spring settling (lower = more precise)
};

/**
 * Animation position states for the share button
 * Each state includes both position values and transition configuration
 * @type {Object}
 */
export const ANIMATION_POSITIONS = {
  visible: {
    y: 0,              // Final position when visible
    opacity: 1,        // Fully visible
    zIndex: 1,         // Above other elements
    transition: {
      type: "spring",
      stiffness: 300,  // Matches SPRING_ANIMATION for consistency
      damping: 25,     // Matches SPRING_ANIMATION for consistency
      mass: 1          // Matches SPRING_ANIMATION for consistency
    }
  },
  hidden: {
    y: 15,             // Slight offset when hidden (reduced from 20 for quicker transition)
    opacity: 0,        // Fully transparent
    zIndex: 0,         // Below other elements
    transition: {
      type: "spring",
      stiffness: 300,  // Matches SPRING_ANIMATION for consistency
      damping: 25,     // Matches SPRING_ANIMATION for consistency
      mass: 1          // Matches SPRING_ANIMATION for consistency
    }
  }
};

/**
 * Modal animation variants for backdrop and content
 * Used by ModalBackdrop and ModalContent components
 * @type {Object}
 */
export const MODAL_VARIANTS = {
  backdrop: {
    hidden: { opacity: 0, backdropFilter: "blur(0px)" },
    visible: { opacity: 1, backdropFilter: "blur(4px)" },
    exit: { opacity: 0, backdropFilter: "blur(0px)" }
  },
  content: {
    hidden: { opacity: 0, scale: 0.9, y: 20 },
    visible: { opacity: 1, scale: 1, y: 0 },
    exit: { opacity: 0, scale: 0.9, y: 20 }
  }
};

/**
 * Fade-in animation variants for page/section entrances
 * Used by FadeIn component
 * @type {Object}
 */
export const FADE_IN_VARIANTS = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

/**
 * Scale animation variants for interactive elements
 * Used by ScaleOnHover component
 * @type {Object}
 */
export const SCALE_VARIANTS = {
  hover: { scale: 1.05 },
  tap: { scale: 0.95 }
};

/**
 * Slide animation variants for sliding elements
 * @type {Object}
 */
export const SLIDE_VARIANTS = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 20 }
};

/**
 * Stagger animation configuration for sequential child animations
 * Used by StaggerContainer component
 * @type {Object}
 */
export const STAGGER_VARIANTS = {
  container: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  },
  item: {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  }
};

/**
 * Slide up animation variants for larger elements (like modals or forms)
 * @type {Object}
 */
export const SLIDE_UP_LARGE_VARIANTS = {
  hidden: { opacity: 0, y: 100 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 20,
      delay: 0.2
    }
  }
};

/**
 * Slide animation variants for elements sliding in from the right
 * @type {Object}
 */
export const SLIDE_RIGHT_VARIANTS = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 50 }
};
